import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  editorStateToHtml,
  htmlToEditorState,
} from '../../../../foundation/components/rich_text_editor/RichTextEditor';
import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import {
  useGetVariantDetailQuery,
  useUnlinkSampleMutation,
  useUpdateSampleMutation,
} from '../../../api/variantApi';
import { VARIANT_STAGE_TYPES } from '../../constants';
import { VariantSample } from '../../redux';
import { getSampleVariantColumns } from '../../utils';

interface UseSampleEditModalLogicProps {
  closeModal: () => void;
  sample?: VariantSample;
}

/**
 * Custom hook that encapsulates all the business logic for SampleEditModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useSampleEditModalLogic = ({
  closeModal,
  sample,
}: UseSampleEditModalLogicProps) => {
  const navigate = useNavigate();

  const {
    isLoading,
    user,
    stageInfo,
    handleSubmit,
    getIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose: closeModal,
    successMessage: 'Sample updated successfully',
    stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  });

  const { vid } = useParams();
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: vid },
    { skip: !vid },
  );

  // Mutation hooks return [trigger, result] tuples.
  // invalidatesTags on each mutation causes RTK Query to refetch VariantSamples
  // automatically on success — no manual state update needed
  const [updateSampleMutation] = useUpdateSampleMutation();
  const [unlinkSampleMutation] = useUnlinkSampleMutation();

  const variantTableColumns = getSampleVariantColumns(navigate);

  // Memoize EditorState for initial load
  const initialEditorState = useMemo(
    () => htmlToEditorState(sample?.comment || ''),
    [sample?.comment],
  );

  // Ensure initialValues.comment matches editor output exactly
  const initialCommentHtml = useMemo(
    () => editorStateToHtml(initialEditorState),
    [initialEditorState],
  );

  // Memoize EditorState for indication field (now a RichTextEditor)
  const initialIndicationEditorState = useMemo(
    () => htmlToEditorState(sample?.indication || ''),
    [sample?.indication],
  );

  const initialValues = useMemo(
    () => ({
      sample_external_id: sample?.sample_external_id || '',
      subject_external_id: sample?.subject_external_id || '',
      indication: sample?.indication || '',
      comment: initialCommentHtml, // <-- EXACTLY matches editor output
    }),
    [sample, initialCommentHtml],
  );

  const modalOpts = {
    ...createModalOpts('Edit Sample'),
  };

  const handleFormSubmit = async (values: any) => {
    if (user && sample?.sample_id && variant?.id) {
      await handleSubmit(async () => {
        await updateSampleMutation({
          sample_id: String(sample.sample_id),
          requestData: {
            indication: values.indication,
            subject_external_id: values.subject_external_id,
            genotype_id: sample.genotype_id,
            comment: values.comment, // already HTML string
          },
          variant_id: variant.id,
        }).unwrap();

        // invalidatesTags fires automatically — RTK Query refetches VariantSamples
      });
    } else {
      displayUnAuthorizedNotification();
      return;
    }
  };

  // Unlink sample from variant
  const handleUnlinkSample = async () => {
    if (
      sample?.sample_id &&
      sample?.genotype_id &&
      variant?.id &&
      window.confirm(
        'Are you sure you want to unlink this sample from the variant?',
      )
    ) {
      await handleSubmit(async () => {
        await unlinkSampleMutation({
          sample_id: String(sample.sample_id),
          genotype_id: String(sample.genotype_id),
          variant_id: variant.id,
        }).unwrap();
      });
    }
  };

  /**
   * Calculates whether the unlink button should be disabled
   * based on user authentication, stage status, and loading state
   * (mirrors save logic but doesn't require form dirtiness)
   */
  const getIsUnlinkDisabled = () => {
    return (
      !user?.id ||
      (stageInfo?.status !== 'revised' &&
        stageInfo?.status !== 'not_started') ||
      isLoading
    );
  };

  return {
    isLoading,
    user,
    stageInfo,
    variant,
    variantTableColumns,
    initialEditorState,
    initialIndicationEditorState,
    initialValues,
    modalOpts,
    getIsSaveDisabled,
    getIsUnlinkDisabled,
    handleFormSubmit,
    handleUnlinkSample,
  };
};

export default useSampleEditModalLogic;
